/**
 * PreviewChip — persistent "MyCraftz Preview · Sample" chip (D24).
 * Rendered inside every React stage screen when the URL carries
 * ?mode=preview. Twin of the iframe overlay chip injected by embed.js
 * on the frozen v2 mockups.
 *
 * Visual philosophy: unobtrusive, parchment-toned, corner-anchored.
 * Does not alter any existing page layout.
 *
 * D26 (English-first with Sanskrit sub-layer) is honoured by the
 * optional `stage` prop — pass e.g. "Learn" / "Bodha" to display the
 * dual label.
 */
import React from "react";

const STAGE_SANSKRIT = {
  Discover: "Jigyasa",
  Learn: "Bodha",
  Commit: "Sankalpa",
  CraftPrint: "CraftPrint",
  Practice: "Sadhana",
  Transform: "Siddhi",
  Experience: "Anubhava",
};

export default function PreviewChip({ stage }) {
  const sanskrit = stage && STAGE_SANSKRIT[stage];
  return (
    <div
      data-testid="preview-chip"
      aria-label="You are viewing a preview with sample content"
      style={{
        position: "fixed",
        top: 14,
        right: 14,
        zIndex: 10000,
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        padding: "8px 14px",
        borderRadius: 999,
        background:
          "linear-gradient(180deg, rgba(246,232,194,0.98) 0%, rgba(216,184,112,0.98) 100%)",
        border: "1px solid #8a6420",
        color: "#1f1108",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        fontSize: "0.85rem",
        letterSpacing: "0.05em",
        boxShadow:
          "0 4px 14px rgba(31,17,8,0.28), inset 0 0 0 1px rgba(255,250,232,0.55)",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, #d6a44a, #8a6420 70%)",
          boxShadow: "inset 0 -1px 2px rgba(58,36,24,0.4)",
        }}
      />
      <span style={{ fontWeight: 600 }}>
        MyCraftz Preview
      </span>
      <span style={{ opacity: 0.7 }}>·</span>
      <span style={{ fontStyle: "italic" }}>Sample</span>
      {stage ? (
        <>
          <span style={{ opacity: 0.5 }}>·</span>
          <span>
            {stage}
            {sanskrit ? (
              <em
                style={{
                  marginLeft: 6,
                  fontStyle: "italic",
                  opacity: 0.75,
                  fontSize: "0.9em",
                }}
              >
                ({sanskrit})
              </em>
            ) : null}
          </span>
        </>
      ) : null}
    </div>
  );
}

/**
 * PreviewIntroBanner — rendered ONCE on the MyCraftz Preview workspace
 * (after Sadhana). Communicates the "This is where all my future
 * CraftPrints will live" message per today's directive.
 */
export function PreviewIntroBanner() {
  return (
    <div
      data-testid="preview-intro-banner"
      style={{
        margin: "16px auto 24px",
        maxWidth: 780,
        padding: "18px 22px",
        borderRadius: 10,
        border: "1px solid #a37e2a",
        background:
          "linear-gradient(180deg, rgba(245,238,220,0.9) 0%, rgba(234,210,154,0.85) 100%)",
        fontFamily: "'Cormorant Garamond', Georgia, serif",
        color: "#1f1108",
        textAlign: "center",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "0.72rem",
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: "#7a5030",
          fontFamily: "'Cinzel', serif",
        }}
      >
        Experience · MyCraftz Preview
      </p>
      <p
        style={{
          margin: "10px 0 6px",
          fontSize: "1.35rem",
          fontWeight: 700,
          fontStyle: "italic",
          color: "#3a2611",
        }}
      >
        This is where all your future CraftPrints will live.
      </p>
      <p style={{ margin: 0, fontSize: "0.95rem", color: "#4a3320" }}>
        Every book you read adds another CraftPrint. Your journey never restarts.
      </p>
    </div>
  );
}
